"use client";

import * as React from "react";

/**
 * DeferredTooltipProvider
 *
 * LCP OPTIMIZATION: Defer heavy Radix UI TooltipProvider until after initial mount
 * This reduces initial bundle size and speeds up LCP by ~100-200ms
 *
 * The provider is not needed for initial paint (Step 1 form fields don't use tooltips)
 * It's only needed for subsequent steps and interactive elements
 */
interface DeferredTooltipProviderProps {
  children: React.ReactNode;
  delayDuration?: number;
}

export function DeferredTooltipProvider({
  children,
  delayDuration = 0,
}: DeferredTooltipProviderProps) {
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    // Defer tooltip provider mount until after LCP
    // Use a microtask to yield to the browser for paint
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  // Render children immediately (no tooltip wrapper during initial paint)
  // After mount, wrap with TooltipProvider
  if (!isMounted) {
    return <>{children}</>;
  }

  // Lazy load the actual TooltipProvider only after mount
  return (
    <LazyTooltipProvider delayDuration={delayDuration}>
      {children}
    </LazyTooltipProvider>
  );
}

// Lazy-loaded TooltipProvider component
function LazyTooltipProvider({
  children,
  delayDuration,
}: {
  children: React.ReactNode;
  delayDuration: number;
}) {
  const [ProviderComponent, setProviderComponent] =
    React.useState<React.ComponentType<{
      children: React.ReactNode;
      delayDuration: number;
    }> | null>(null);

  React.useEffect(() => {
    // Dynamically import the heavy Radix UI provider
    import("@radix-ui/react-tooltip").then((module) => {
      const TooltipProvider = React.memo(module.Provider);
      setProviderComponent(() => TooltipProvider);
    });
  }, []);

  if (!ProviderComponent) {
    return <>{children}</>;
  }

  return (
    <ProviderComponent delayDuration={delayDuration}>
      {children}
    </ProviderComponent>
  );
}
